import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, CheckCircle } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const SecuriteReglementation = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/guides"
          className="inline-flex items-center text-primary-red hover:text-primary-red-dark mb-8 transition-colors"
        >
          <ArrowLeft className="mr-2" size={20} />
          Retour aux guides
        </Link>

        <article className="prose prose-lg max-w-none">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Sécurité et réglementation : parier en toute légalité en France
          </h1>

          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p>
              La sécurité et la réglementation sont des aspects fondamentaux des paris en ligne en France. 
              Depuis 2010, le secteur est strictement encadré par l'Autorité Nationale des Jeux (ANJ), 
              qui garantit la légalité, la sécurité et la protection des joueurs. Comprendre ce cadre 
              réglementaire est essentiel pour parier en toute sérénité et éviter les pièges des sites 
              non autorisés.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              L'agrément ANJ : votre garantie de légalité
            </h2>

            <p>
              L'Autorité Nationale des Jeux est l'organisme qui régule et contrôle tous les opérateurs de 
              paris en ligne en France. Pour opérer légalement, chaque site doit obtenir un agrément ANJ, 
              qui est délivré après une procédure rigoureuse de vérification. Cet agrément garantit que 
              l'opérateur respecte toutes les obligations légales et réglementaires.
            </p>

            <p>
              Vérifier l'agrément d'un site est simple : il doit être visible sur le site, généralement 
              en bas de page, avec un numéro d'agrément. Vous pouvez aussi consulter directement le 
              registre officiel de l'ANJ sur leur site internet pour confirmer qu'un opérateur est bien 
              autorisé. Ne jamais parier sur un site sans agrément ANJ : c'est illégal et vous n'avez 
              aucune protection en cas de problème.
            </p>

            <p>
              Les opérateurs agréés sont soumis à des contrôles réguliers de l'ANJ, qui vérifie leur 
              conformité aux règles, leur santé financière, et leur traitement des joueurs. Si un 
              opérateur ne respecte pas ses obligations, l'ANJ peut suspendre ou révoquer son agrément. 
              Cette supervision continue garantit un niveau de sécurité élevé pour les joueurs.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              La protection de vos données personnelles
            </h2>

            <p>
              Les opérateurs agréés sont tenus de protéger vos données personnelles selon le Règlement 
              Général sur la Protection des Données (RGPD). Vos informations sont cryptées lors des 
              transmissions, stockées de manière sécurisée, et ne sont utilisées que dans le cadre strict 
              de la relation commerciale et des obligations légales (lutte contre le blanchiment, vérification 
              d'identité).
            </p>

            <p>
              Lors de la création de votre compte, vous devez fournir des informations personnelles : 
              nom, prénom, date de naissance, adresse, coordonnées bancaires. Ces données sont nécessaires 
              pour vérifier votre identité (obligation légale), traiter vos paiements, et vous protéger 
              contre la fraude. Les opérateurs sérieux utilisent des technologies de cryptage avancées 
              pour sécuriser ces informations.
            </p>

            <p>
              Vous avez le droit d'accéder à vos données, de les rectifier, et de demander leur suppression 
              dans certains cas. Les opérateurs agréés doivent respecter ces droits et vous expliquer 
              clairement comment exercer ces droits. Si vous avez des doutes sur le traitement de vos 
              données, contactez le service client ou consultez la politique de confidentialité du site.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              La sécurisation des transactions financières
            </h2>

            <p>
              Toutes les transactions financières sur les sites agréés sont sécurisées. Les opérateurs 
              utilisent des protocoles de paiement sécurisés (SSL, cryptage) et travaillent avec des 
              prestataires de paiement reconnus et régulés. Vos coordonnées bancaires ne sont jamais 
              stockées en clair et sont protégées par plusieurs couches de sécurité.
            </p>

            <p>
              Les méthodes de paiement acceptées par les opérateurs agréés sont toutes sécurisées : cartes 
              bancaires, virements, portefeuilles électroniques. Chaque méthode a ses propres garanties 
              de sécurité. Les portefeuilles électroniques comme PayPal ou Skrill ajoutent une couche 
              supplémentaire de protection en n'exposant pas directement vos coordonnées bancaires au site 
              de paris.
            </p>

            <p>
              En cas de transaction suspecte ou non autorisée, contactez immédiatement votre banque et 
              l'opérateur. Les sites agréés ont des procédures pour gérer ces situations et vous aider 
              à résoudre le problème rapidement. Gardez toujours une trace de vos transactions et vérifiez 
              régulièrement vos relevés bancaires.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Les obligations des opérateurs envers les joueurs
            </h2>

            <p>
              Les opérateurs agréés ont plusieurs obligations légales envers leurs clients. Ils doivent 
              fournir des informations claires sur les conditions générales, les règles des paris, et les 
              probabilités de gain. Ils doivent aussi proposer des outils de contrôle : limites de dépôt, 
              d'exclusion temporaire ou définitive, rappels de temps de jeu.
            </p>

            <p>
              L'information sur le jeu responsable est obligatoire. Les sites doivent afficher des messages 
              de prévention, des liens vers les associations d'aide, et des informations sur les risques. 
              Ils doivent aussi détecter et intervenir en cas de comportement de jeu problématique, même 
              si le joueur n'a pas demandé d'aide.
            </p>

            <p>
              En cas de litige, les opérateurs doivent proposer une procédure de réclamation claire et 
              accessible. Si le litige n'est pas résolu directement, vous pouvez saisir le médiateur des 
              jeux en ligne, un organisme indépendant qui aide à résoudre les conflits entre joueurs et 
              opérateurs. Cette procédure est gratuite et accessible à tous.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Comment parier en ligne en France en toute sécurité
            </h2>

            <p>
              Pour parier en ligne en France en toute sécurité, commencez toujours par vérifier l'agrément 
              ANJ du site. Ne créez jamais de compte sur un site sans agrément, même si les offres semblent 
              attractives. Les sites non autorisés ne respectent pas la réglementation française et vous 
              n'avez aucune protection en cas de problème.
            </p>

            <p>
              Utilisez des mots de passe forts et uniques pour votre compte de paris. Activez l'authentification 
              à deux facteurs si disponible : cette fonctionnalité ajoute une couche de sécurité importante 
              en exigeant un code supplémentaire lors de la connexion. Ne partagez jamais vos identifiants 
              avec qui que ce soit.
            </p>

            <p>
              Vérifiez régulièrement l'historique de votre compte : dépôts, paris, retraits. Si vous 
              remarquez une activité suspecte, contactez immédiatement le service client. Les opérateurs 
              agréés ont des procédures pour gérer ces situations et protéger votre compte.
            </p>

            <div className="bg-gray-50 rounded-xl p-6 mt-8 space-y-4">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Shield className="mr-2 text-primary-red" size={24} />
                Checklist de sécurité
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-gray-900">Vérifier l'agrément ANJ avant de créer un compte</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-gray-900">Utiliser un mot de passe fort et unique</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-gray-900">Activer l'authentification à deux facteurs</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-gray-900">Vérifier régulièrement l'historique de votre compte</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-gray-900">Ne jamais partager vos identifiants</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-gray-900">Utiliser les outils de contrôle disponibles</p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Que faire en cas de problème
            </h2>

            <p>
              Si vous rencontrez un problème avec un opérateur agréé, contactez d'abord son service client. 
              La plupart des problèmes peuvent être résolus rapidement par ce biais. Gardez une trace de 
              vos échanges (emails, numéros de ticket) pour référence future.
            </p>

            <p>
              Si le problème persiste ou si vous n'obtenez pas satisfaction, vous pouvez saisir le médiateur 
              des jeux en ligne. Cet organisme indépendant examine les litiges entre joueurs et opérateurs 
              et propose des solutions. La procédure est gratuite et accessible en ligne. Le médiateur peut 
              imposer une solution à l'opérateur si celui-ci est en tort.
            </p>

            <p>
              Pour les problèmes de sécurité (fraude, compte piraté), contactez aussi votre banque et, 
              si nécessaire, déposez une plainte auprès des autorités compétentes. Les opérateurs agréés 
              ont l'obligation de signaler les incidents de sécurité et de coopérer avec les autorités.
            </p>

            <div className="bg-primary-red/10 border-l-4 border-primary-red p-6 mt-12 rounded-r-lg">
              <p className="text-gray-800 leading-relaxed">
                <strong>En résumé :</strong> La réglementation française des paris en ligne est stricte 
                et protectrice. En choisissant un opérateur agréé par l'ANJ, vous bénéficiez de garanties 
                de sécurité, de protection de vos données, et d'un cadre légal clair. Respectez les bonnes 
                pratiques de sécurité, utilisez les outils de contrôle disponibles, et n'hésitez pas à 
                faire valoir vos droits en cas de problème. Avec ces précautions, vous pouvez parier en 
                ligne en France en toute sérénité.
              </p>
            </div>
          </div>
        </article>

        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link
            to="/guides"
            data-discover="true"
            className="inline-flex items-center text-primary-red hover:text-primary-red-dark transition-colors"
          >
            <ArrowLeft className="mr-2" size={20} />
            Retour à la liste des guides
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SecuriteReglementation;

